/**
 * LA TABLE DE VALEURS — capacité `CAP-09` (tranche T7 du chantier
 * « mathématiques de bout en bout »).
 *
 * Une table x / y — ou rang / terme, ou temps / distance — se rencontre à
 * trois endroits, et c'est le MÊME objet :
 *
 *  - AFFICHÉE dans une fiche : une illustration de genre `table`, partout où
 *    une fiche admet une illustration ;
 *  - AFFICHÉE dans un énoncé : le champ `table` d'un exercice ;
 *  - À REMPLIR : la nature de réponse `table`. L'élève reçoit la table avec
 *    des cases vides ; la correction juge CHAQUE case, sans modèle.
 *
 * L'outil « Table de valeurs » de l'atelier graphique n'est PAS cet objet : il
 * pose des points sur une courbe déjà tracée. Il n'est ni réemployé, ni changé.
 *
 * Une table est une liste de LIGNES de même longueur ; chaque ligne a un nom
 * (texte riche court : `\(x\)`, « Rang », « Temps (s) ») et des valeurs, qui
 * sont des écritures (`3`, `-2{,}5`, `\dfrac{1}{2}`). Elle se dessine en
 * colonnes : une colonne par valeur, les noms en tête de ligne.
 *
 * Module PUR : ni rendu, ni schéma (les schémas vivent avec leurs formats).
 */
/** Ce qu'une case devient à la correction. */
export const ETATS_DE_CASE = ['donnee', 'juste', 'a_revoir', 'vide'];
export const LIGNES_DE_TABLE = { min: 2, max: 4 };
export const COLONNES_DE_TABLE = { min: 2, max: 10 };
/** Ce qu'un schéma ne voit pas d'une table : des lignes de longueurs différentes, une case hors table ou répétée. */
export function griefsDeTable(table, aRemplir = []) {
    const griefs = [];
    const colonnes = table.lignes[0]?.valeurs.length ?? 0;
    table.lignes.forEach((l, i) => {
        if (l.valeurs.length !== colonnes) {
            griefs.push(`ligne ${i + 1} : ${l.valeurs.length} valeur(s), ${colonnes} attendue(s) — toutes les lignes ont la même longueur`);
        }
    });
    const vues = new Set();
    aRemplir.forEach((c, k) => {
        const cle = cleDeCase(c);
        if (c.ligne >= table.lignes.length || c.colonne >= colonnes) {
            griefs.push(`case à remplir ${k + 1} : (${c.ligne} ; ${c.colonne}) est hors de la table`);
        }
        else if (vues.has(cle)) {
            griefs.push(`case à remplir ${k + 1} : (${c.ligne} ; ${c.colonne}) est déjà à remplir`);
        }
        vues.add(cle);
    });
    return griefs;
}
export const cleDeCase = (c) => `${c.ligne}:${c.colonne}`;
/** La table servie à l'élève : les cases à remplir vidées — la réponse ne voyage pas. */
export function tableARemplir(table, aRemplir) {
    const vides = new Set(aRemplir.map(cleDeCase));
    return {
        lignes: table.lignes.map((l, i) => ({
            nom: l.nom,
            valeurs: l.valeurs.map((v, j) => (vides.has(`${i}:${j}`) ? null : v)),
        })),
    };
}
/**
 * CE QUE L'ÉLÈVE ENVOIE : ses cases, en une écriture — `1:2=7 ; 1:3=9`
 * (ligne:colonne=valeur, à partir de 0). Une case laissée vide n'y est pas.
 * Le séparateur est le point-virgule : la virgule reste décimale.
 */
export function ecrireCases(cases) {
    return cases
        .filter((c) => c.valeur.trim() !== '')
        .map((c) => `${cleDeCase(c)}=${c.valeur.trim()}`)
        .join(' ; ');
}
/** Lit des cases écrites par `ecrireCases`. `null` : ce n'est pas cette écriture. */
export function lireCases(texte) {
    const cases = new Map();
    const brut = (texte ?? '').trim();
    if (brut === '')
        return cases;
    for (const morceau of brut.split(';')) {
        const m = /^\s*(\d{1,2}):(\d{1,2})\s*=\s*(.+?)\s*$/.exec(morceau);
        if (!m || cases.has(`${Number(m[1])}:${Number(m[2])}`))
            return null;
        cases.set(`${Number(m[1])}:${Number(m[2])}`, m[3]);
    }
    return cases;
}
/** Le texte nu d'un nom de ligne ou d'une valeur : sans délimiteurs ni `\\text`, la virgule décimale rendue. */
const nu = (riche) => riche
    .replace(/\\\(|\\\)|\$/g, '')
    .replace(/\{,\}/g, ',')
    .replace(/\\text\{([^{}]*)\}/g, '$1')
    .replace(/\\[dt]?frac\{([^{}]*)\}\{([^{}]*)\}/g, '$1/$2')
    .replace(/[{}]/g, '')
    .trim();
/**
 * La table, dite à qui ne la voit pas : « x : 0 ; 1 ; 2. y : 1 ; 3 ; case à
 * remplir. » — une ligne par phrase.
 */
export function direTable(table) {
    const matrice = 'matrice' in table && table.matrice !== undefined;
    const corps = table.lignes
        .map((l, i) => `${matrice ? `ligne ${i + 1}` : nu(l.nom)} : ${l.valeurs.map((v) => (v === null ? 'case à remplir' : nu(v))).join(' ; ')}.`)
        .join(' ');
    return matrice
        ? `${direDimensions(table.lignes.length, table.lignes[0]?.valeurs.length ?? 0)}. ${corps}`
        : corps;
}
/** « Matrice de deux lignes et trois colonnes » (tranche T41). */
function direDimensions(lignes, colonnes) {
    const dit = ['zéro', 'une', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit'];
    const compte = (n, nom) => `${dit[n] ?? String(n)} ${nom}${n > 1 ? 's' : ''}`;
    return `Matrice ${lignes === 1 ? 'd’' : 'de '}${compte(lignes, 'ligne')} et ${compte(colonnes, 'colonne')}`;
}
/** La table corrigée, dite : chaque case avec ce qu'elle est devenue. */
export function direTableCorrigee(table) {
    const matrice = table.matrice !== undefined;
    const corps = table.lignes
        .map((l, i) => `${matrice ? `ligne ${i + 1}` : nu(l.nom)} : ${l.cases
        .map((c) => {
        const v = c.valeur === null ? 'rien' : nu(c.valeur);
        if (c.etat === 'juste')
            return `${v}, juste`;
        if (c.etat === 'a_revoir')
            return `${v}, à revoir`;
        if (c.etat === 'vide')
            return 'case laissée vide';
        return v;
    })
        .join(' ; ')}.`)
        .join(' ');
    return matrice
        ? `${direDimensions(table.lignes.length, table.lignes[0]?.cases.length ?? 0)}. ${corps}`
        : corps;
}
/* ═════════════════════════════════════ la matrice comme réponse (tranche T41) */
/**
 * LA MATRICE À REMPLIR (nature `matrice`) n'est pas une table de valeurs : ni
 * nom de ligne, ni règle de x à y — une grille de dimensions données, que
 * l'élève remplit EN ENTIER. Elle emprunte pourtant toute la mécanique de la
 * table : l'écriture des cases (`1:2=7 ; …`), la correction case par case, la
 * grille corrigée qui voyage dans la copie, l'historique et la console du
 * tuteur. Un seul moteur de cases ; la marque `matrice` change le dessin.
 */
export const LIGNES_DE_MATRICE = { min: 1, max: 6 };
export const COLONNES_DE_MATRICE = { min: 1, max: 8 };
/** La grille servie à l'élève : toutes les cases vides, aucun nom — la réponse ne voyage pas. */
export function matriceARemplir(lignes, colonnes, delimiteur = 'parentheses') {
    return {
        lignes: Array.from({ length: lignes }, () => ({
            nom: '',
            valeurs: Array.from({ length: colonnes }, () => null),
        })),
        matrice: delimiteur,
    };
}
/** Une matrice en texte riche : `\(\begin{pmatrix} 0 & -1 \\ 1 & 0 \end{pmatrix}\)`. */
export function matriceEnLatex(m) {
    const env = m.delimiteur === 'crochets' ? 'bmatrix' : 'pmatrix';
    return `\\(\\begin{${env}} ${m.lignes.map((l) => l.join(' & ')).join(' \\\\ ')} \\end{${env}}\\)`;
}
/** Les cases d'une matrice complète, dans l'écriture de l'élève : `0:0=0 ; 0:1=-1 ; …`. */
export function ecrireMatrice(lignes) {
    return ecrireCases(lignes.flatMap((l, ligne) => l.map((valeur, colonne) => ({ ligne, colonne, valeur }))));
}
/**
 * Lit une matrice écrite en LaTeX — `\begin{pmatrix} 0 & -1 \\ 1 & 0 \end{pmatrix}`
 * (ou `bmatrix`), délimiteurs `\( \)` admis — en lignes de cases. `null` : ce
 * n'est pas une matrice, ou ses lignes sont de longueurs inégales.
 */
export function lireMatriceLatex(texte) {
    const m = /^\s*(?:\\\()?\s*\\begin\{([pb])matrix\}([\s\S]*?)\\end\{\1matrix\}\s*(?:\\\))?\s*$/.exec(texte ?? '');
    if (m === null)
        return null;
    const brutes = m[2].split('\\\\');
    if (brutes.length > 1 && brutes[brutes.length - 1].trim() === '')
        brutes.pop();
    const lignes = brutes.map((l) => l.split('&').map((c) => c.trim()));
    const colonnes = lignes[0]?.length ?? 0;
    if (colonnes === 0 || lignes.some((l) => l.length !== colonnes || l.some((c) => c === ''))) {
        return null;
    }
    return { lignes, delimiteur: m[1] === 'b' ? 'crochets' : 'parentheses' };
}
//# sourceMappingURL=table-valeurs.js.map